import React from 'react';
import {
  Stepper as MaterialStepper,
  StepConnector,
  Step,
  StepLabel,
  StepIconProps,
  withStyles,
  makeStyles,
  createStyles,
  Theme,
  Grid,
  Typography,
} from '@material-ui/core';
import { Check, X } from 'react-feather';
import clsx from 'clsx';
import colors from '../../../theme/colors';

export type StepperProps = {
  /** An array containing the steps to use */
  steps: string[];
  /** The current active step offset */
  activeStep: number;
  /** If true, the active step will be rendered as an error */
  error?: boolean;
  /** Id for component */
  id?: string;
};

interface StyledStepIconProps extends StepIconProps {
  error: boolean;
  id: string;
}

/**
 * Connector styles
 */
const StyledConnector = withStyles({
  line: {
    height: 2,
    border: 0,
    backgroundColor: colors.madison,
  },
  alternativeLabel: {
    top: 12,
    left: 'calc(-50% + 12px)',
    right: 'calc(50% + 12px)',
  },
})(StepConnector);

/**
 * Individual step Icon definition
 */

type StyleProps = {
  error: boolean;
};

const useStyledStepIconStyles = makeStyles({
  baseStep: {
    color: colors.white,
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: `2px solid ${colors.silver}`,
  },
  active: ({ error }: StyleProps) =>
    error
      ? {
          border: 'none',
          backgroundColor: colors.bright_pink,
        }
      : {
          border: `4px solid ${colors.bright_turquoise}`,
          backgroundColor: colors.white,
        },
  activeGrid: {
    width: 24,
    height: 24,
  },
  completedStep: {
    width: 24,
    height: 24,
    border: 'none',
    backgroundColor: colors.shamrock,
  },
});
const StyledStep = ({
  active,
  completed,
  error,
  id,
}: StyledStepIconProps): JSX.Element => {
  const classes = useStyledStepIconStyles({ error });
  return (
    <div
      className={clsx(classes.baseStep, {
        [classes.active]: active,
        [classes.completedStep]: completed,
      })}
    >
      {error && !completed && (
        <Grid
          id={id}
          container
          className={classes.activeGrid}
          alignItems="center"
          justifyContent="center"
        >
          <X data-testid="error-x" size={16} color={colors.white} />
        </Grid>
      )}
      {completed && (
        <Grid
          id={id}
          container
          className={classes.activeGrid}
          alignItems="center"
          justifyContent="center"
        >
          <Check size={16} />
        </Grid>
      )}
    </div>
  );
};

/**
 * The stepper definition
 */
const useStepStyles = makeStyles((theme: Theme) =>
  createStyles({
    stepper: {
      background: 'transparent',
      padding: `${theme.spacing(2)}px 0`,
    },
    label: {
      wordBreak: 'break-word',
    },
  }),
);
const Stepper = ({
  steps,
  activeStep,
  error = false,
  id,
}: StepperProps): JSX.Element => {
  const classes = useStepStyles();
  return (
    <MaterialStepper
      id={id}
      alternativeLabel
      className={classes.stepper}
      activeStep={activeStep}
      connector={<StyledConnector />}
    >
      {steps.map(
        (label: string, offset: number): React.ReactNode => (
          <Step
            data-testid={`step-${label}`}
            key={label}
            id={`${id}-${offset}`}
          >
            <StepLabel
              data-testid={`step-label-${label}`}
              StepIconComponent={StyledStep}
              StepIconProps={{
                id: `${id}-icon-${offset}`,
                error: error && offset === activeStep,
              }}
            >
              <Typography variant="caption" className={classes.label}>
                {label}
              </Typography>
            </StepLabel>
          </Step>
        ),
      )}
    </MaterialStepper>
  );
};

export default Stepper;
